/**
 * THE ONE GRID. 09 §5.4 — binding, product-wide. A module that needs a variant files a design
 * exception, not a private implementation.
 *
 *   T1  RTL: headers and labels on the RIGHT; the first (rightmost) column identifies the row.
 *   T2  Sticky header for the whole scroll.
 *   T3  ONE scroll owner — the shell gives the grid the page's only scrollport.
 *   T4  Wide content scrolls horizontally in its own container; the page body never does.
 *   T5  Sticky identifying column — in RTL that is the RIGHTMOST column.
 *   T6  Totals row last, always, as a general engine behaviour, and it respects the active filter.
 *   T8  A row with a child screen: the WHOLE row is the target and a chevron sits at the LEFT end.
 *   T12 Zero rows is a state, not an empty grid.
 *
 * Numbers follow 09 §9.2: tabular figures, LTR digits inside an RTL cell (explicitly controlled,
 * never left to the browser's bidi guess), and negatives in PARENTHESES in red — never a minus sign,
 * which in an RTL table renders on the wrong side and turns `-500` into something that reads `500-`.
 */

import type { ReactNode } from 'react';

export interface Column<Row> {
  readonly key: string;
  /** 09 §9.1: a noun phrase in office Hebrew. */
  readonly headerHe: string;
  readonly render: (row: Row) => ReactNode;
  /** Summable ⇒ T6 gives the grid a totals row. */
  readonly total?: (rows: readonly Row[]) => number;
  readonly numeric?: boolean;
}

export interface DataTableProps<Row> {
  readonly captionHe: string;
  readonly columns: readonly Column<Row>[];
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row) => string;
  /** T8 — present ⇒ the whole row opens the child screen. */
  readonly childHref?: (row: Row) => string;
  /** T12 — zero rows is a written state (09 §5.8), never headers with nothing under them. */
  readonly emptyStateHe: string;
  /** 09 §5.3: the chip row states the effect in words. */
  readonly filterSummaryHe?: string;
}

/**
 * 09 §9.2: exactly 2 decimals, comma thousands, negatives in parentheses and red, zero is `0.00`
 * and never a blank cell.
 */
export function formatAmount(value: number): string {
  const abs = Math.abs(value).toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `(${abs})` : abs;
}

export function Amount({ value }: { readonly value: number | null }): JSX.Element {
  if (value === null) {
    // 09 §9.2: null is an em-dash, visually distinct from zero.
    return (
      <span className="cell-null" title="אין נתון">
        —
      </span>
    );
  }
  return (
    // dir="ltr" is explicit: bidi is controlled, never inferred (09 §9.5 A1).
    <span className={value < 0 ? 'cell-amount cell-amount--negative' : 'cell-amount'} dir="ltr">
      {formatAmount(value)}
    </span>
  );
}

export function DataTable<Row>(props: DataTableProps<Row>): JSX.Element {
  const { captionHe, columns, rows, rowKey, childHref, emptyStateHe, filterSummaryHe } = props;

  if (rows.length === 0) {
    // T12 — a grid with headers and nothing under them reads as a failure.
    return (
      <section className="state-block state-block--empty">
        <h2 className="state-block__title">{captionHe}</h2>
        <p className="state-block__body">{emptyStateHe}</p>
      </section>
    );
  }

  const hasTotals = columns.some((c) => c.total !== undefined);

  return (
    <div className="grid">
      {filterSummaryHe === undefined ? null : <p className="grid__filter-summary">{filterSummaryHe}</p>}
      {/* T4 — the horizontal scrollport lives here, not on the page body. */}
      <div className="grid__scroll">
        <table className="grid__table">
          <caption className="grid__caption">{captionHe}</caption>
          <thead className="grid__head">
            <tr>
              {columns.map((c, i) => (
                <th
                  key={c.key}
                  scope="col"
                  // T1/T5 — the first column in RTL source order is the rightmost, and it sticks.
                  className={i === 0 ? 'grid__th grid__th--identity' : 'grid__th'}
                  data-numeric={c.numeric === true ? 'true' : undefined}
                >
                  {c.headerHe}
                </th>
              ))}
              {childHref === undefined ? null : <th className="grid__th grid__th--chevron" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} className="grid__row">
                {columns.map((c, i) => (
                  <td
                    key={c.key}
                    className={i === 0 ? 'grid__td grid__td--identity' : 'grid__td'}
                    data-numeric={c.numeric === true ? 'true' : undefined}
                  >
                    {i === 0 && childHref !== undefined ? (
                      // T8 — the whole row is the target; the link fills the identifying cell.
                      <a className="grid__row-link" href={childHref(row)}>
                        {c.render(row)}
                      </a>
                    ) : (
                      c.render(row)
                    )}
                  </td>
                ))}
                {childHref === undefined ? null : (
                  // T8 — the chevron sits at the LEFT end of the row: in RTL, forward is leftwards.
                  <td className="grid__td grid__td--chevron" aria-hidden="true">
                    ‹
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {hasTotals ? (
            // T6 — totals last, always, visually separated, respecting the active filter
            // (the rows handed in are already the filtered rows).
            <tfoot className="grid__foot">
              <tr>
                {columns.map((c, i) => (
                  <td key={c.key} className="grid__td grid__td--total" data-numeric={c.numeric === true ? 'true' : undefined}>
                    {c.total === undefined ? (i === 0 ? 'סה״כ' : null) : <Amount value={c.total(rows)} />}
                  </td>
                ))}
                {childHref === undefined ? null : <td className="grid__td" />}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}
